/**
 * Utility functions for debug mode timestamp comparisons.
 * These are pure functions that can be easily tested.
 */

/**
 * Determines if debug mode is currently active based on the expiration timestamp.
 *
 * @param debugModeUntil - ISO datetime string or null
 * @returns true if debug mode is active (timestamp is in the future)
 */
export function isDebugModeActive(debugModeUntil: string | null): boolean {
  if (!debugModeUntil) {
    return false;
  }

  const expirationDate = new Date(debugModeUntil);

  // Check for invalid date
  if (Number.isNaN(expirationDate.getTime())) {
    return false;
  }

  return expirationDate.getTime() > Date.now();
}

/**
 * Calculates the remaining time in milliseconds until debug mode expires.
 * Returns 0 if debug mode is not active or already expired.
 *
 * @param debugModeUntil - ISO datetime string or null
 * @returns milliseconds until expiration, or 0 if expired/inactive
 */
export function getDebugModeRemainingMs(debugModeUntil: string | null): number {
  if (!debugModeUntil) {
    return 0;
  }

  const expirationDate = new Date(debugModeUntil);

  // Check for invalid date
  if (Number.isNaN(expirationDate.getTime())) {
    return 0;
  }

  const remainingMs = expirationDate.getTime() - Date.now();
  return Math.max(0, remainingMs);
}
